import { Box, Divider, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import { useGoalAndOperationalPlansForm } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider';
import { formatToShortThaiDateTime } from '@Utils/datetime';
import { CompleteSectionFormValues } from '@Utils/Types/FormValuesTypes';
import {
  validateEnglishDescription,
  validateThaiDescription,
} from '@Utils/validation';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import {
  checkCompanyValueIncreasePlansOverviewEnValid,
  checkCompanyValueIncreasePlansOverviewThValid,
  checkOverviewEnValid,
  checkOverviewThValid,
  checkValueUpPlansCompleteEn,
  checkValueUpPlansCompleteTh,
} from './utils';
import { resetCompletedSection } from '@Utils/updateCompletedSection';

const GoalAndOperationalPlansCompletionCheckbox = () => {
  const { isPublished } = useProgressBar();
  const { setValue, control } = useFormContext();
  const completedSection = useWatch({
    name: 'completedSection',
  }) as CompleteSectionFormValues;

  const [overview, companyValueIncreasePlans, otherDescriptions] = useWatch({
    control,
    name: ['overview', 'companyValueIncreasePlans', 'otherDescriptions'],
  });

  const {
    isCompletedTh,
    isCompletedEn,
    dateTh,
    dateEn,
    roleStampTh,
    roleStampEn,
  } = completedSection || {};
  const { valueUpPlans } = companyValueIncreasePlans || {};
  const formattedCompletedSectionDateTh = dateTh
    ? formatToShortThaiDateTime(dateTh)
    : '';

  const formattedCompletedSectionDateEn = dateEn
    ? formatToShortThaiDateTime(dateEn)
    : '';

  useEffect(() => {
    setValue('isAllCompletedTh', isCompletedTh ?? false);
    setValue('isAllCompletedEn', isCompletedEn ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedTh, isCompletedEn]);

  const isGoalsCompleteTh = checkValueUpPlansCompleteTh(valueUpPlans);
  const isGoalsCompleteEn = checkValueUpPlansCompleteEn(valueUpPlans);

  const isOverviewEnValid = overview ? checkOverviewEnValid(overview) : true;
  const isOverviewThValid = overview ? checkOverviewThValid(overview) : true;

  const isCompanyValueIncreasePlansOverviewEnValid =
    checkCompanyValueIncreasePlansOverviewEnValid(companyValueIncreasePlans);
  const isCompanyValueIncreasePlansOverviewThValid =
    checkCompanyValueIncreasePlansOverviewThValid(companyValueIncreasePlans);

  const isOtherDescriptionThValid = otherDescriptions
    ? validateThaiDescription(otherDescriptions)
    : true;
  const isOtherDescriptionEnValid = otherDescriptions
    ? validateEnglishDescription(otherDescriptions)
    : true;

  const isIntroRemarkEnValid =
    isOverviewEnValid && isCompanyValueIncreasePlansOverviewEnValid;

  const isIntroRemarkThValid =
    isOverviewThValid && isCompanyValueIncreasePlansOverviewThValid;

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: 'Th' | 'En'
  ) => {
    const updatedSection = { ...completedSection };

    if (language === 'Th') {
      updatedSection.isCompletedTh = e.target.checked;
    } else {
      updatedSection.isCompletedEn = e.target.checked;
    }

    resetCompletedSection(updatedSection, setValue);
  };

  return (
    <Box bgcolor={'common.white'}>
      <Typography fontWeight={800} p={2}>
        กรณีต้องการยืนยันความถูกต้อง
      </Typography>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight="bold" textAlign={'right'}>
            ส่วนการยืนยันการตรวจสอบข้อมูลครบถ้วน
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              inputProps={{
                disabled:
                  isPublished ||
                  !isGoalsCompleteTh ||
                  !isOtherDescriptionThValid ||
                  !isIntroRemarkThValid,
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'Th')
              }
            />
            {isCompletedTh && dateTh && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampTh} {formattedCompletedSectionDateTh}
              </Typography>
            )}
          </Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              inputProps={{
                disabled:
                  isPublished ||
                  !isGoalsCompleteEn ||
                  !isIntroRemarkEnValid ||
                  !isOtherDescriptionEnValid,
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'En')
              }
            />
            {isCompletedEn && dateEn && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampEn} {formattedCompletedSectionDateEn}
              </Typography>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default GoalAndOperationalPlansCompletionCheckbox;
